<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { ItemDTO } from "@barkup/shared";
import { useItemList } from "../composables/useItemList.js";
import { useInfiniteScroll } from "../composables/useInfiniteScroll.js";
import { groupItemsByDate } from "../format.js";
import BusyToast from "../components/BusyToast.vue";
import FilterBar from "../components/FilterBar.vue";
import SelectionBar from "../components/SelectionBar.vue";
import ItemCard from "../components/ItemCard.vue";
import ItemTile from "../components/ItemTile.vue";
import ItemActionsSheet from "../components/ItemActionsSheet.vue";
import ItemDetailView from "../components/ItemDetailView.vue";
import ConfirmDialog from "../components/ConfirmDialog.vue";
import { setReportTarget } from "../reportTarget.js";

const {
  items,
  filters,
  query,
  view,
  selected,
  selectionMode,
  busyLabel,
  errorLabel,
  loadError,
  loading,
  hasMore,
  listState,
  searchPending,
  searchingWholeArchive,
  searchIncomplete,
  dateFilterActive,
  clearSearch,
  clearDates,
  loadMore,
  toggleSelect,
  enterSelection,
  cancelSelection,
  deleteSelected,
  deleteOne,
  download,
  retry,
} = useItemList({ trash: false });

const grouped = computed(() => groupItemsByDate(items.value));

// Auto-pagination — the sentinel sits under the list; guard so a fired
// intersection during an in-flight load doesn't double-request.
const { sentinel } = useInfiniteScroll(() => {
  if (hasMore.value && !loading.value) void loadMore();
});

// ⋮ bottom sheet — one instance for the whole view, `null` = closed.
const sheetItem = ref<ItemDTO | null>(null);

const route = useRoute();
const router = useRouter();

const activeItemId = computed(() => (typeof route.query.item === "string" ? route.query.item : null));
const activeIndex = computed(() => items.value.findIndex((i) => i.id === activeItemId.value));
const activeItem = computed(() => (activeIndex.value >= 0 ? items.value[activeIndex.value] : null));
const positionLabel = computed(() => {
  if (activeIndex.value < 0) return "";
  return hasMore.value ? `${activeIndex.value + 1}` : `${activeIndex.value + 1} / ${items.value.length}`;
});

function openItem(id: string) {
  // Dismiss the ⋮ sheet directly (not just via its own close emit) so opening
  // the detail never depends on that sheet's leave transition completing — a
  // stalled transition previously left the sheet covering the just-opened
  // detail, reading as "tapped เปิดดู, nothing happened".
  sheetItem.value = null;
  router.push({ query: { ...route.query, item: id } });
}
function closeItem() {
  router.back();
}
function goToIndex(index: number) {
  const target = items.value[index];
  if (target) router.replace({ query: { ...route.query, item: target.id } });
}
function showPrev() {
  if (activeIndex.value > 0) goToIndex(activeIndex.value - 1);
}
async function showNext() {
  const idx = activeIndex.value;
  if (idx < 0) return;
  if (idx >= items.value.length - 1) {
    if (!hasMore.value) return;
    await loadMore();
  }
  goToIndex(idx + 1);
}
// After delete, the list is reloaded fresh — the same index now holds whatever
// item took the deleted one's place, which reads as "advance to next."
async function handleDelete(id: string) {
  const idx = items.value.findIndex((i) => i.id === id);
  await deleteOne(id);
  const next = items.value[idx] ?? items.value[idx - 1];
  if (next) router.replace({ query: { ...route.query, item: next.id } });
  else closeItem();
}

/**
 * Delete confirmation (DESIGN.md §9.2 is about takedowns; this is plain UX):
 * deleting used to fire on a single tap of a menu row or a pill sitting right
 * next to ดาวน์โหลด, with the only undo buried in another tab. `pendingDelete`
 * holds what a confirmed dialog would remove — an id for one item, "selection"
 * for the whole selection.
 */
const pendingDelete = ref<string | "selection" | null>(null);
const deleteCount = computed(() => (pendingDelete.value === "selection" ? selected.size : 1));

async function confirmDelete() {
  const target = pendingDelete.value;
  if (!target) return;
  pendingDelete.value = null;
  // Deleting from inside the detail view needs to move the reader onto a
  // neighbouring item afterwards; a list-level delete just reloads.
  if (target !== "selection" && target === activeItemId.value) await handleDelete(target);
  else if (target === "selection") await deleteSelected();
  else await deleteOne(target);
}

/**
 * Reporting moved to its own screen (/report/:itemId) — see ReportView. The
 * item is parked for that screen to render context, along with the list URL to
 * return to, stripped of ?item= so the reported (now hidden) item's detail
 * can't be reopened from history.
 */
function openReport(item: ItemDTO) {
  sheetItem.value = null;
  const { item: _omit, ...rest } = route.query;
  setReportTarget(item, router.resolve({ path: route.path, query: rest }).fullPath);
  router.push({ name: "report", params: { itemId: item.id } });
}
</script>

<template>
  <div class="flex flex-col gap-stack-md">
    <!-- While selecting, selecting is the task — the contextual bar replaces the filter strip. -->
    <SelectionBar
      v-if="selectionMode"
      :count="selected.size"
      :trash="false"
      :busy="!!busyLabel"
      @cancel="cancelSelection"
      @remove="pendingDelete = 'selection'"
    />
    <FilterBar
      v-else
      :category="filters.category"
      :date-from="filters.dateFrom"
      :date-to="filters.dateTo"
      :q="filters.q"
      :pending="searchPending"
      @update:category="filters.category = $event"
      @update:date-from="filters.dateFrom = $event"
      @update:date-to="filters.dateTo = $event"
      @update:q="filters.q = $event"
    />

    <!-- Skeleton rows, not a spinner — the shape of the content that's coming.
         Raised only for a load with nothing to show (first open, tab switch);
         a search never reaches here, because the local pass has already painted. -->
    <div v-if="listState === 'skeleton'" class="flex flex-col gap-2" aria-label="กำลังโหลด" role="status">
      <div v-for="n in 6" :key="n" class="skeleton-row flex items-center gap-3 rounded-xl px-2 py-2">
        <span class="h-10 w-10 shrink-0 rounded-lg bg-surface-container-high" />
        <span class="flex min-w-0 flex-1 flex-col gap-1.5">
          <span class="h-4 rounded bg-surface-container-high" :style="{ width: `${55 + ((n * 13) % 30)}%` }" />
          <span class="h-3 w-1/3 rounded bg-surface-container" />
        </span>
      </div>
    </div>
    <!-- A failed first load must not masquerade as an empty archive — offer a retry. -->
    <div v-else-if="listState === 'error'" class="flex flex-col items-center gap-3 py-16 text-center">
      <span class="material-symbols-outlined text-[40px] text-outline-variant">cloud_off</span>
      <p class="font-body-sm text-on-surface-variant">{{ loadError }}</p>
      <button
        type="button"
        class="rounded-full border border-outline-variant px-4 py-2 font-label-caps text-on-surface transition-colors hover:bg-surface-container-low"
        @click="retry"
      >
        ลองอีกครั้ง
      </button>
    </div>
    <!-- Nothing matched locally *yet*. Not an empty state and not a skeleton —
         saying "ไม่พบ" here would be a claim the app can't back up until the
         server answers, and it would flip to results a moment later. -->
    <div v-else-if="listState === 'searching'" class="flex flex-col items-center gap-2 py-16 text-center" role="status">
      <span class="spin text-[20px] text-on-surface-variant" />
      <p class="font-body-sm text-on-surface-variant">กำลังค้นในคลังทั้งหมด...</p>
    </div>
    <!-- A search that genuinely found nothing. Its own state: the archive isn't
         empty, the query just missed, so the way out is clearing the query. -->
    <div v-else-if="listState === 'no-results'" class="flex flex-col items-center gap-3 py-16 text-center">
      <span class="material-symbols-outlined text-[40px] text-outline-variant">search_off</span>
      <!-- `break-words`: the query is user input on a 360px-wide phone, and a
           pasted URL with no spaces in it would otherwise run off the screen. -->
      <p class="max-w-[30ch] break-words font-body-sm text-on-surface-variant">
        ไม่พบรายการที่ตรงกับ “<span class="font-semibold text-on-surface">{{ query }}</span>”
      </p>
      <p class="max-w-[30ch] font-metadata text-on-surface-variant">ค้นได้จากชื่อไฟล์ ชื่อลิงก์ และที่อยู่ลิงก์เท่านั้น</p>
      <!-- The query may not be what missed. A date range narrows the search
           silently, and blaming the words alone sends the user rewording a
           query that was right all along. -->
      <p v-if="dateFilterActive" class="max-w-[30ch] font-metadata text-on-surface-variant">
        ค้นเฉพาะในช่วงวันที่ที่เลือกอยู่
      </p>
      <div class="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          class="rounded-full border border-outline-variant px-4 py-2 font-label-caps text-on-surface transition-colors hover:bg-surface-container-low"
          @click="clearSearch"
        >
          ล้างคำค้นหา
        </button>
        <button
          v-if="dateFilterActive"
          type="button"
          class="rounded-full border border-outline-variant px-4 py-2 font-label-caps text-on-surface transition-colors hover:bg-surface-container-low"
          @click="clearDates"
        >
          ล้างตัวกรองวันที่
        </button>
      </div>
    </div>
    <!-- A date range that matched nothing. The archive is not empty and the
         files are not gone — one filter is in the way, and the chip that says
         so may have scrolled out of view. -->
    <div v-else-if="listState === 'filtered-empty'" class="flex flex-col items-center gap-3 py-16 text-center">
      <span class="material-symbols-outlined text-[40px] text-outline-variant">event_busy</span>
      <p class="max-w-[30ch] font-body-sm text-on-surface-variant">ไม่มีรายการในช่วงวันที่ที่เลือก</p>
      <button
        type="button"
        class="rounded-full border border-outline-variant px-4 py-2 font-label-caps text-on-surface transition-colors hover:bg-surface-container-low"
        @click="clearDates"
      >
        ล้างตัวกรองวันที่
      </button>
    </div>
    <div v-else-if="listState === 'empty'" class="flex flex-col items-center gap-2 py-16 text-center">
      <span class="material-symbols-outlined text-[40px] text-outline-variant">inventory_2</span>
      <p class="font-body-sm text-on-surface-variant">ยังไม่มีไฟล์ในคลังนี้</p>
      <p class="max-w-[28ch] font-metadata text-on-surface-variant">
        ส่งไฟล์ รูป หรือลิงก์ในแชท แล้ว BarkUp จะเก็บเข้าคลังให้อัตโนมัติ
      </p>
    </div>

    <template v-else>
      <section v-for="group in grouped" :key="group.label" class="flex flex-col gap-1">
        <h2 class="px-2 pt-1 font-label-caps uppercase tracking-wider text-on-surface-variant">{{ group.label }}</h2>

        <div v-if="view === 'list'" class="flex flex-col">
          <ItemCard
            v-for="item in group.items"
            :key="item.id"
            :item="item"
            :selected="selected.has(item.id)"
            :selection-mode="selectionMode"
            :highlight="query"
            @toggle-select="toggleSelect"
            @enter-select="enterSelection"
            @open="openItem"
            @menu="sheetItem = $event"
          />
        </div>
        <div v-else class="grid grid-cols-3 gap-1.5">
          <ItemTile
            v-for="item in group.items"
            :key="item.id"
            :item="item"
            :selected="selected.has(item.id)"
            :selection-mode="selectionMode"
            :highlight="query"
            @toggle-select="toggleSelect"
            @enter-select="enterSelection"
            @open="openItem"
          />
        </div>
      </section>

      <!-- These matches came from what's already on the device, and the archive
           is bigger than that. One quiet line instead of a result count that
           would only have to correct itself a moment later. -->
      <p
        v-if="searchingWholeArchive"
        class="py-3 text-center font-metadata text-on-surface-variant"
        aria-hidden="true"
      >
        กำลังค้นต่อในคลังทั้งหมด...
      </p>
      <!-- The rows above are real matches; the pass that would have found the
           rest failed. Say which half is missing rather than leaving "กำลังค้นต่อ"
           spinning on a promise the app knows it can't keep. -->
      <div v-else-if="searchIncomplete" class="flex flex-col items-center gap-2 py-3 text-center" role="status">
        <p class="max-w-[32ch] font-metadata text-on-surface-variant">
          ค้นได้เฉพาะรายการที่โหลดไว้แล้ว — ค้นในคลังทั้งหมดไม่สำเร็จ
        </p>
        <button
          type="button"
          class="rounded-full border border-outline-variant px-4 py-1.5 font-label-caps text-on-surface transition-colors hover:bg-surface-container-low"
          @click="retry"
        >
          ลองอีกครั้ง
        </button>
      </div>
      <!-- Auto-load sentinel; the row also doubles as the "loading more" indicator. -->
      <div
        v-else-if="hasMore"
        ref="sentinel"
        class="py-3 text-center font-metadata text-on-surface-variant"
        role="status"
      >
        {{ loading ? "กำลังโหลดเพิ่ม..." : "" }}
      </div>
    </template>

    <ItemActionsSheet
      :item="sheetItem"
      :trash="false"
      @close="sheetItem = null"
      @open="openItem"
      @download="download"
      @select="enterSelection"
      @remove="pendingDelete = $event"
      @report="openReport"
    />

    <BusyToast :label="busyLabel ?? errorLabel" :error="!!errorLabel" />

    <ConfirmDialog
      :open="pendingDelete !== null"
      :title="deleteCount > 1 ? `ย้าย ${deleteCount} รายการไปถังขยะ?` : 'ย้ายไปถังขยะ?'"
      message="ไฟล์จะอยู่ในถังขยะ 30 วัน กู้คืนได้ตลอดช่วงนั้น หลังจากนั้นจะถูกลบถาวร"
      confirm-label="ย้ายไปถังขยะ"
      danger
      @cancel="pendingDelete = null"
      @confirm="confirmDelete"
    />

    <ItemDetailView
      v-if="activeItem"
      :item="activeItem"
      :trash="false"
      :has-prev="activeIndex > 0"
      :has-next="activeIndex < items.length - 1 || hasMore"
      :position="positionLabel"
      @close="closeItem"
      @prev="showPrev"
      @next="showNext"
      @delete="pendingDelete = $event"
      @download="download"
      @report="openReport(activeItem)"
    />
  </div>
</template>

<style scoped>
.skeleton-row {
  animation: skeleton-pulse 1.4s ease-in-out infinite;
}
@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton-row {
    animation: none;
    opacity: 0.8;
  }
}
</style>
